import { EventType } from "@/analytics/analytics-events";
import { formatDistance } from "@/components/common/settings-context/distance-unit-utils";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import { useThreeContext } from "@/components/common/three-context/three-context";
import { AnalysisReportScene } from "@/components/r3f/renderers/analyses/analysis-report-scene";
import { getAnalysisTypeByValue } from "@/components/r3f/renderers/analyses/colormap-reference-plane-panel";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { PointCloudObject } from "@/object-cache";
import {
  PointCloudAnalysis,
  ReferencePlaneType,
} from "@/store/point-cloud-analysis-tool-slice";
import {
  FaroDialog,
  FaroDialogProps,
  FaroRichTextEditor,
  TextField,
} from "@faro-lotv/flat-ui";
import { Analytics, useTranslate } from "@faro-lotv/foreign-observers";
import { assert } from "@faro-lotv/foundation";
import { Stack } from "@mui/material";
import { domToCanvas } from "modern-screenshot";
import { JSX, useCallback, useEffect, useRef, useState } from "react";
import { PdfReportData } from "./use-save-pdf-report";
type CreatePdfReportDialogProps = Pick<
  FaroDialogProps,
  "open" | "onClose" | "onCancel" | "onConfirm"
> & {
  /** Callback to pass back the generated report (image + text + title); or pass an error string in case of failure */
  setReportData(reportData: PdfReportData | string): void;

  /** Point Cloud to snapshot */
  pointCloud: PointCloudObject;

  /** Analysis associated with the Point Cloud */
  analysis: PointCloudAnalysis;
};

const MAX_TITLE_CHARACTERS = 100;

/**
 * Dialog for creating a PDF report from a 3D view and a rich-text description.
 * Displays a 3D view snapshot alongside a rich-text editor.
 *
 * @returns {JSX.Element} The dialog component for PDF report creation.
 */
export function CreatePdfReportDialog({
  pointCloud,
  analysis,
  open,
  setReportData,
  onConfirm,
  onCancel,
  onClose,
}: CreatePdfReportDialogProps): JSX.Element {
  const { handleErrorWithToast } = useErrorHandlers();

  // Stores rich-text description for the report
  const [reportText, setReportText] = useState("");
  const [titleText, setTitleText] = useState("Analysis Report");

  const [creatingStarted, setCreatingStarted] = useState(false);

  const titleRef = useRef<HTMLInputElement>(null);

  // Focus on Title when dialog opens
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        if (titleRef.current) {
          titleRef.current.focus();
          titleRef.current.select();
        }
      });
    }
  }, [open]);

  // Access ThreeJS renderer from context
  const { renderer } = useThreeContext();

  // create a ref to the wrapper div that contains AnalysisReportScene
  const reportRef = useRef<HTMLDivElement | null>(null);

  const distanceUnit = useDistanceUnit();

  // Handle generating snapshot and passing back to parent
  const generateReport = useCallback(async () => {
    const pdfReportCanvas = reportRef.current;

    if (pdfReportCanvas && pdfReportCanvas instanceof HTMLElement && renderer) {
      Analytics.track(EventType.createColoredAnalysisReport);

      // Wait for the next frame to allow renderer to be ready
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => resolve());
      });

      // Upscale canvas factor for the snapshot to ensure high-resolution image output.
      const candidateScale = 4;

      // maximum width and height of the snapshot in pixels
      // = 300 dpi * 8 inch-wide image
      const maxWidth = 2400;
      const maxHeight = 2400;

      const screenRatio =
        renderer.domElement.width / renderer.domElement.height;

      // Prepare canvas for rendering snapshot
      const result = document.createElement("canvas");
      const candidateWidth = Math.min(
        candidateScale * renderer.domElement.width,
        maxWidth,
      );
      const candidateHeight = Math.min(candidateWidth / screenRatio, maxHeight);
      result.width = candidateHeight * screenRatio;
      result.height = candidateHeight;
      const scale = result.width / renderer.domElement.width;

      const context = result.getContext("2d");

      if (context) {
        // Draw ThreeJS renderer's view first
        context.drawImage(
          renderer.domElement,
          0,
          0,
          result.width,
          result.height,
        );

        // Capture HTML elements on top (like annotations) into another canvas
        const canvas = await domToCanvas(pdfReportCanvas, {
          backgroundColor: null,
          scale:
            (renderer.domElement.width / pdfReportCanvas.clientWidth) * scale,
          // The 3D scene is drawn from the renderer directly, so all canvases
          // are omitted from the capture of the HTML on top of it.
          filter: (node) => !(node instanceof HTMLCanvasElement),
        });

        context.drawImage(canvas, 0, 0);

        // Convert final canvas to PNG base64
        const base64Image = result.toDataURL("image/png");

        let analysisType = getAnalysisTypeByValue(analysis.referencePlaneType);
        assert(analysisType, "unexpected analysis type");
        if (analysis.referencePlaneType === ReferencePlaneType.elevation) {
          analysisType += ` (${formatDistance(analysis.elevation, distanceUnit)})`;
        }

        // Provide back to parent alongside rich-text
        setReportData({
          imageDataUrl: base64Image,
          description: reportText,
          title: titleText,
          analysisType,
        });
      }
    }

    // Confirm and close the dialog
    onConfirm?.();
  }, [
    renderer,
    onConfirm,
    analysis.referencePlaneType,
    analysis.elevation,
    setReportData,
    reportText,
    titleText,
    distanceUnit,
  ]);

  // maximum width of the dialog contents in pixels
  const sceneMaxWidth = 496;
  // maximum height of the AnalysisReportScene contents before a vertical scrollbar is required by default
  const sceneMaxHeight = 512;

  const translate = useTranslate();

  return (
    <FaroDialog
      title="Create report"
      variant="info"
      dark
      open={open}
      disabled={creatingStarted}
      onConfirm={() => {
        setCreatingStarted(true);
        // defer report creation to provide immediate disable of buttons
        setTimeout(() => {
          generateReport();
        }, 0);
      }}
      onCancel={onCancel}
      onClose={onClose}
      confirmText={creatingStarted ? "Creating" : "Create"}
      isConfirmDisabled={
        !titleText.length || titleText.length > MAX_TITLE_CHARACTERS
      }
      cancelText="Discard"
      showSpinner={creatingStarted}
    >
      <Stack direction="column">
        <Stack direction="row" sx={{ justifyContent: "space-around" }}>
          <Stack
            direction="row"
            sx={{ justifyContent: "space-around" }}
            ref={reportRef}
          >
            {/* Display the 3D view to snapshot */}
            <AnalysisReportScene
              pointCloud={pointCloud}
              analysis={analysis}
              sceneMaxWidth={sceneMaxWidth}
              sceneMaxHeight={sceneMaxHeight}
            />
          </Stack>
        </Stack>

        <TextField
          inputRef={titleRef}
          text={titleText}
          fullWidth
          variant="outlined"
          label="Title"
          tag="star"
          error={titleText.length ? undefined : "Title is required"}
          dark
          placeholder="Insert a title"
          sx={{
            height: "fit-content",
            minHeight: "32px",
            input: { fontSize: "1rem" },
          }}
          onTextChanged={setTitleText}
          maxCharacterCount={MAX_TITLE_CHARACTERS}
          shouldShowCounter
          multiline
          inputProps={{ style: { fontSize: "1rem" } }}
        />

        {/* Rich-text editor for adding a description to the report */}
        <FaroRichTextEditor
          dark
          fullWidth
          label="Description"
          placeholder={translate("Insert a description")}
          onChange={setReportText}
          sx={{ height: "20vh" }}
          onError={(error) =>
            handleErrorWithToast({
              title: "Invalid or unsupported content",
              error,
            })
          }
        />
      </Stack>
    </FaroDialog>
  );
}
